import { useState } from 'react';
import { Icon } from './Icon';

export type FieldKind = 'text' | 'select' | 'segment' | 'textarea' | 'chips';

export interface FieldDef {
  kind: FieldKind;
  key: string;
  label: string;
  value?: string;
  placeholder?: string;
  full?: boolean;
  options?: string[]; // segment / select
  chips?: string[];
}

const LABEL_STYLE = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-3)',
  textTransform: 'uppercase' as const,
  letterSpacing: '.04em',
  marginBottom: 7,
  display: 'block',
};

const INPUT_STYLE = {
  border: '1px solid var(--border-2)',
  background: 'var(--surface)',
  borderRadius: 8,
  padding: '9px 11px',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
};

interface FieldProps {
  def: FieldDef;
  value: string | string[];
  onChange: (val: string | string[]) => void;
}

export function Field({ def, value, onChange }: FieldProps) {
  return (
    <div style={{ gridColumn: def.full ? '1 / -1' : 'auto' }}>
      <label style={LABEL_STYLE}>{def.label}</label>
      {def.kind === 'text' && (
        <input value={value as string} placeholder={def.placeholder} onChange={(e) => onChange(e.target.value)} style={INPUT_STYLE} />
      )}
      {def.kind === 'textarea' && (
        <textarea value={value as string} placeholder={def.placeholder} onChange={(e) => onChange(e.target.value)} rows={3} style={{ ...INPUT_STYLE, resize: 'vertical' }} />
      )}
      {def.kind === 'select' && <SelectField def={def} value={value as string} onChange={onChange} />}
      {def.kind === 'segment' && <SegmentField def={def} value={value as string} onChange={onChange} />}
      {def.kind === 'chips' && <ChipsField value={(value as string[]) || []} onChange={onChange} />}
    </div>
  );
}

function SelectField({ def, value, onChange }: { def: FieldDef; value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        className="cc-clickable"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border-2)', background: 'var(--surface)', borderRadius: 8, padding: '9px 11px' }}
      >
        <span style={{ fontSize: 13, color: value ? 'var(--text)' : 'var(--text-3)' }}>{value || def.placeholder || 'Select…'}</span>
        <Icon name="expand_more" size={18} color="var(--text-3)" />
      </div>
      {open && def.options && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', zIndex: 10, maxHeight: 220, overflowY: 'auto' }}>
          {def.options.map((o) => (
            <div key={o} className="cc-hover-s2 cc-clickable" onClick={() => { onChange(o); setOpen(false); }} style={{ padding: '9px 11px', fontSize: 13 }}>
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SegmentField({ def, value, onChange }: { def: FieldDef; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3, gap: 3 }}>
      {(def.options || []).map((o) => {
        const sel = o === value;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            style={{ appearance: 'none', cursor: 'pointer', border: 0, borderRadius: 6, padding: '6px 14px', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, background: sel ? 'var(--surface)' : 'transparent', color: sel ? 'var(--text)' : 'var(--text-3)', boxShadow: sel ? 'var(--shadow-sm)' : 'none' }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function ChipsField({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', border: '1px solid var(--border-2)', background: 'var(--surface)', borderRadius: 8, padding: 8 }}>
      {value.map((c) => (
        <span key={c} className="cc-mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px solid var(--border)', padding: '3px 5px 3px 8px', borderRadius: 5 }}>
          {c}
          <span onClick={() => onChange(value.filter((x) => x !== c))} style={{ display: 'inline-flex', cursor: 'pointer' }}>
            <Icon name="close" size={14} color="var(--text-3)" />
          </span>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder="Add…"
        style={{ border: 0, outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--text)', flex: 1, minWidth: 60 }}
      />
    </div>
  );
}
